
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff } from "lucide-react";
import { PasswordStrengthIndicator } from "./shared/PasswordStrengthIndicator";
import { secureSignInSchema, secureSignUpSchema } from "../schemas/secureAuthSchema";
import type { z } from "zod";
import type { FieldError, FieldErrorsImpl } from "react-hook-form";

type SignInFormData = z.infer<typeof secureSignInSchema>;
type SignUpFormData = z.infer<typeof secureSignUpSchema>;

interface AuthFormProps {
  onSubmit: (data: SignInFormData | SignUpFormData) => void;
  isLoading: boolean;
  mode: "login" | "signup";
  error?: string | null;
}

// Helper function to safely extract error message string
const getErrorMessage = (error: FieldError | FieldErrorsImpl<any> | undefined): string | undefined => {
  if (!error) return undefined;
  
  // Handle standard FieldError
  if ('message' in error && typeof error.message === 'string') {
    return error.message;
  }
  
  // Handle nested error objects
  if (typeof error === 'object' && error !== null) {
    // Try to find a message in the error object
    const errorObj = error as any;
    if (errorObj.message && typeof errorObj.message === 'string') {
      return errorObj.message;
    }
  }
  
  return undefined;
};

export const AuthForm: React.FC<AuthFormProps> = ({
  onSubmit,
  isLoading,
  mode,
  error
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const schema = mode === "signup" ? secureSignUpSchema : secureSignInSchema;
  
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid }
  } = useForm({
    resolver: zodResolver(schema),
    mode: "onChange"
  });

  const watchedPassword = watch("password", "");

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {mode === "signup" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="first_name">First Name</Label>
            <Input
              {...register("first_name")}
              placeholder="Enter your first name"
              className={errors.first_name ? "border-destructive" : ""}
            />
            {errors.first_name && (
              <p className="text-sm text-destructive">{getErrorMessage(errors.first_name)}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="last_name">Last Name</Label>
            <Input
              {...register("last_name")}
              placeholder="Enter your last name"
              className={errors.last_name ? "border-destructive" : ""}
            />
            {errors.last_name && (
              <p className="text-sm text-destructive">{getErrorMessage(errors.last_name)}</p>
            )}
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          {...register("email")}
          type="email"
          placeholder="Enter your email"
          className={errors.email ? "border-destructive" : ""}
        />
        {errors.email && (
          <p className="text-sm text-destructive">{getErrorMessage(errors.email)}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="password">Password</Label>
        <div className="relative">
          <Input
            {...register("password")}
            type={showPassword ? "text" : "password"}
            placeholder={mode === "signup" ? "Create a strong password" : "Enter your password"}
            className={errors.password ? "border-destructive pr-10" : "pr-10"}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </Button>
        </div>
        {errors.password && (
          <p className="text-sm text-destructive">{getErrorMessage(errors.password)}</p>
        )}
        
        {/* Show password strength indicator for signup */}
        {mode === "signup" && watchedPassword && (
          <PasswordStrengthIndicator password={watchedPassword} />
        )}
      </div>

      {mode === "signup" && (
        <div className="space-y-2">
          <Label htmlFor="confirmPassword">Confirm Password</Label>
          <div className="relative">
            <Input
              {...register("confirmPassword")}
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm your password"
              className={errors.confirmPassword ? "border-destructive pr-10" : "pr-10"}
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
          </div>
          {errors.confirmPassword && (
            <p className="text-sm text-destructive">{getErrorMessage(errors.confirmPassword)}</p>
          )}
        </div>
      )}

      {error && (
        <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
          {error}
        </div>
      )}

      <Button
        type="submit"
        className="w-full"
        disabled={isLoading || (mode === "signup" && !isValid)}
      >
        {isLoading
          ? "Processing..."
          : mode === "signup"
          ? "Create Account"
          : "Sign In"
        }
      </Button>
    </form>
  );
};
